import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { useToast } from '../components/ToastProvider';
import './CustomerLogin.css';

const CustomerLogin = () => {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('email'); // email or otp
    const [loading, setLoading] = useState(false);
    const { login: authLogin, checkAuth } = useCustomerAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const handleSendOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';
            const res = await fetch(`${BASE_URL}/api/customer/send-otp.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();

            if (data.ok) {
                setStep('otp');
                showToast('OTP sent to your email!', 'success');
            } else {
                showToast(data.message || 'Failed to send OTP', 'error');
            }
        } catch (err) {
            showToast('Something went wrong. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';
            const res = await fetch(`${BASE_URL}/api/customer/verify-otp.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, otp }),
                credentials: 'include'
            });
            const data = await res.json();

            if (data.ok) {
                showToast('Login successful!', 'success');

                // IMMEDIATELY update context with returned data to avoid race conditions with cookies/me.php
                // authLogin takes (customerData, csrfToken)
                if (data.customer && data.csrf_token) {
                    await authLogin(data.customer, data.csrf_token);
                } else {
                    // Fallback to checkAuth if for some reason data is missing (shouldn't happen with new backend)
                    await checkAuth();
                }

                const searchParams = new URLSearchParams(location.search);
                const redirectPath = searchParams.get('redirect');

                if (redirectPath && redirectPath.startsWith('/account')) {
                    navigate(redirectPath);
                } else if (location.state?.from) {
                    navigate(location.state.from, { state: location.state });
                } else {
                    navigate('/account');
                }
            } else {
                showToast(data.message || 'Invalid OTP', 'error');
            }
        } catch (err) {
            showToast('Verification failed.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <h1>Welcome Back</h1>
                <p className="login-subtitle">Log in to track orders and manage details</p>

                {step === 'email' ? (
                    <form onSubmit={handleSendOtp} className="login-form">
                        <div className="form-group">
                            <label>Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email"
                                required
                                disabled={loading}
                            />
                        </div>
                        <button type="submit" className="btn-login" disabled={loading}>
                            {loading ? 'Sending...' : 'Send OTP'}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={handleVerifyOtp} className="login-form">
                        <div className="form-group">
                            <label>Enter OTP</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                placeholder="6-digit code"
                                maxLength={6}
                                required
                                disabled={loading}
                            />
                            <span className="otp-hint">Check your email for the code</span>
                        </div>
                        <div className="otp-actions">
                            <button type="submit" className="btn-login" disabled={loading}>
                                {loading ? 'Verifying...' : 'Verify & Login'}
                            </button>
                            <button
                                type="button"
                                className="btn-link"
                                onClick={() => setStep('email')}
                                disabled={loading}
                            >
                                Change Email
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default CustomerLogin;
